import { Observable } from 'rxjs';

/**
 * RxJs Operator that buffers up incomming events, and throttle outgoing events with a min time interval.
 *
 * This is usefull if incommin event are tricking resource extensive computation, and we
 * for some reason want to leave some 'space' to do other things.
 */
export function throttledBuffer<T>(throttleTimeMs = 100) {
	return function (source: Observable<T>): Observable<T> {
		return new Observable((subscriber) => {
			const buffer: T[] = [];
			let bufferTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
			let sourceCompleted = false;

			const actionAdd = (value: T): void => {
				buffer.push(value);
				actionStartTimer();
			};

			const flushBufferedItem = (): void => {
				if (buffer.length === 0) {
					if (sourceCompleted) {
						subscriber.complete();
					}
					return;
				}
				const item = buffer.shift();
				if (item) {
					subscriber.next(item);
				}
				actionStartTimer();
			};

			const actionStartTimer = (): void => {
				if (bufferTimeout) {
					return;
				}
				bufferTimeout = setTimeout(() => {
					actionStopTimer();
					flushBufferedItem();
				}, throttleTimeMs);
			};

			const actionStopTimer = (): void => {
				clearTimeout(bufferTimeout);
				bufferTimeout = undefined;
			};

			const actionCleanup = (): void => {
				buffer.length = 0;
				actionStopTimer();
			};

			const sourceSubscription = source.subscribe({
				next(value) {
					actionAdd(value);
				},
				error(error) {
					actionCleanup();
					subscriber.error(error);
				},
				complete() {
					sourceCompleted = true;
				}
			});

			subscriber.add(() => {
				sourceSubscription.unsubscribe();
				actionCleanup();
			});
		});
	};
}
